import { z } from "zod";
import { completionDeliveryIdSchema } from "./completionDelivery";
import { completionUndoLookupResponseSchema, parseCompletionUndoLookupResponse } from "./completionUndoLookup";
import { localizeAiMessage } from "@/src/features/ai-chat/aiResponseLocalization";

export const completionUndoRequestSchema = z.object({
  reactId: completionDeliveryIdSchema, previewId: completionDeliveryIdSchema,
}).strict();

export const completionUndoActionSchema = z.object({
  action: z.literal("undo_component"), status: z.literal("pending"),
  undoRequest: completionUndoRequestSchema,
  componentId: completionDeliveryIdSchema, undoTarget: completionUndoLookupResponseSchema,
}).passthrough().superRefine((action, ctx) => {
  try {
    parseCompletionUndoLookupResponse({ expectedOwnerId: action.undoTarget.ownerId, expectedCompanyId: action.undoTarget.companyId,
      componentId: action.componentId }, action.undoTarget);
    if (!action.undoTarget.undo || action.undoTarget.undo.state !== "ready") throw new Error("Undo is not ready");
  } catch { ctx.addIssue({ code: "custom", message: "Invalid prepared undo target" }); }
});
export type CompletionUndoAction = z.infer<typeof completionUndoActionSchema>;

export function parseCompletionUndoAction(value: unknown, ownerId: string, companyId: string): CompletionUndoAction {
  const action = completionUndoActionSchema.parse(value);
  parseCompletionUndoLookupResponse({ expectedOwnerId: ownerId, expectedCompanyId: companyId, componentId: action.componentId }, action.undoTarget);
  return action;
}

/** Distinct source edits must survive deduplication even when their target matches. */
export function completionUndoActionKey(value: unknown): string | null {
  const parsed = completionUndoActionSchema.safeParse(value);
  if (!parsed.success) return null;
  const { componentId, undoTarget, undoRequest } = parsed.data;
  const source = undoTarget.undo!.source;
  return JSON.stringify(["undo", undoRequest.reactId, undoRequest.previewId, undoTarget.ownerId, undoTarget.companyId, componentId, source.jobId, source.sourceFingerprint, source.effectId, undoTarget.undo!.workspaceSceneId]);
}

/** Persist an honest preparation outcome while the client-side commit is pending. */
export function getPreparedUndoNarration(components: readonly unknown[], language?: string | null) {
  const pending = components.some(value => value && typeof value === "object" && (value as Record<string, unknown>).action === "undo_component");
  return pending ? { textAnswer: localizeAiMessage(language, "component_undo_ready"), audioAnswer: "" } : null;
}
